
import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Save, Loader2, Plus, X } from 'lucide-react';
import ImageUpload from './ImageUpload';

interface ContentEditorProps {
    sectionKey: string;
    title: string;
}

export default function ContentEditor({ sectionKey, title }: ContentEditorProps) {
    const [content, setContent] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
    const [newFieldKey, setNewFieldKey] = useState('');
    const [isAddingField, setIsAddingField] = useState(false);

    useEffect(() => {
        fetchContent();
    }, [sectionKey]);

    const fetchContent = async () => {
        setLoading(true);
        try {
            const { data, error } = await supabase
                .from('site_content')
                .select('content')
                .eq('section_key', sectionKey)
                .maybeSingle();

            if (error) throw error;

            if (data) {
                setContent(data.content);
            } else {
                // Initialize if empty
                setContent({});
            }
        } catch (err) {
            console.error('Error fetching content:', err);
            setMessage({ type: 'error', text: 'Failed to load content' });
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        setMessage(null);
        try {
            const { error } = await supabase
                .from('site_content')
                .upsert({
                    section_key: sectionKey,
                    content: content,
                    updated_at: new Date().toISOString()
                });

            if (error) throw error;
            setMessage({ type: 'success', text: 'Content saved successfully!' });
        } catch (err) {
            console.error('Error saving content:', err);
            setMessage({ type: 'error', text: 'Failed to save content' });
        } finally {
            setSaving(false);
        }
    };

    const handleChange = (key: string, value: string) => {
        setContent((prev: any) => ({
            ...prev,
            [key]: value
        }));
    };

    const handleAddField = () => {
        if (!newFieldKey) return;
        const key = newFieldKey.trim();
        // If key implies typingTexts or similar, init with array
        if (key === 'typingTexts' || key === 'tags' || key === 'technologies') {
            handleChange(key, [] as any);
        } else {
            handleChange(key, '');
        }
        setNewFieldKey('');
        setIsAddingField(false);
    };

    if (loading) {
        return (
            <div className="flex justify-center p-12">
                <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
        );
    }

    return (
        <div className="rounded-xl border border-gray-800 bg-gray-900 p-6">
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-white">{title}</h2>
                <div className="flex gap-2">
                    <button
                        onClick={() => setIsAddingField(true)}
                        className="flex items-center gap-2 rounded-md bg-gray-800 px-3 py-2 text-sm font-medium text-gray-300 hover:bg-gray-700"
                    >
                        <Plus className="h-4 w-4" /> Add Field
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-50"
                    >
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        Save Changes
                    </button>
                </div>
            </div>

            {message && (
                <div className={`mb-6 rounded-md p-4 ${message.type === 'success' ? 'bg-green-900/30 text-green-400 border border-green-900' : 'bg-red-900/30 text-red-400 border border-red-900'
                    }`}>
                    {message.text}
                </div>
            )}

            {isAddingField && (
                <div className="mb-6 p-4 bg-gray-800 rounded-lg border border-gray-700 flex items-end gap-4">
                    <div className="flex-grow">
                        <label className="block text-sm font-medium text-gray-400 mb-1">Field Name (e.g., profileImage, title)</label>
                        <input
                            type="text"
                            value={newFieldKey}
                            onChange={(e) => setNewFieldKey(e.target.value)}
                            className="block w-full rounded-md border-0 bg-gray-900 py-2 px-3 text-white shadow-sm ring-1 ring-inset ring-gray-600 focus:ring-2 focus:ring-blue-500 sm:text-sm"
                            placeholder="Enter field name..."
                        />
                    </div>
                    <button
                        onClick={handleAddField}
                        className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-md text-sm font-medium"
                    >
                        Add
                    </button>
                    <button
                        onClick={() => setIsAddingField(false)}
                        className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-md text-sm font-medium"
                    >
                        Cancel
                    </button>
                </div>
            )}

            <div className="space-y-6">
                {content && Object.keys(content).map((key) => (
                    <div key={key} className="relative group">
                        <div className="flex justify-between mb-2">
                            <label className="block text-sm font-medium text-gray-400 capitalize">
                                {key.replace(/_/g, ' ')}
                            </label>
                            <button
                                onClick={() => {
                                    const newContent = { ...content };
                                    delete newContent[key];
                                    setContent(newContent);
                                }}
                                className="text-xs text-red-500 opacity-0 group-hover:opacity-100 transition-opacity hover:underline"
                            >
                                Remove
                            </button>
                        </div>

                        {Array.isArray(content[key]) ? (
                            <div className="space-y-2">
                                {content[key].map((item: string, index: number) => (
                                    <div key={index} className="flex gap-2">
                                        <input
                                            type="text"
                                            value={item}
                                            onChange={(e) => {
                                                const newArray = [...content[key]];
                                                newArray[index] = e.target.value;
                                                handleChange(key, newArray as any);
                                            }}
                                            className="block w-full rounded-md border-0 bg-gray-800 py-1.5 px-3 text-white shadow-sm ring-1 ring-inset ring-gray-700 placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:text-sm sm:leading-6"
                                        />
                                        <button
                                            onClick={() => {
                                                const newArray = content[key].filter((_: any, i: number) => i !== index);
                                                handleChange(key, newArray as any);
                                            }}
                                            className="p-2 text-red-500 hover:bg-red-900/20 rounded-md transition-colors"
                                        >
                                            <X size={16} />
                                        </button>
                                    </div>
                                ))}
                                <button
                                    onClick={() => {
                                        const newArray = [...content[key], ''];
                                        handleChange(key, newArray as any);
                                    }}
                                    className="flex items-center gap-1 text-xs text-blue-500 hover:text-blue-400 font-medium"
                                >
                                    <Plus size={14} /> Add Item
                                </button>
                            </div>
                        ) : key === 'description' || key === 'bio' || key === 'typingTexts' ? (
                            <div className="space-y-2">
                                <textarea
                                    rows={key === 'typingTexts' ? 2 : 4}
                                    value={typeof content[key] === 'string' ? content[key] : JSON.stringify(content[key])}
                                    onChange={(e) => handleChange(key, e.target.value)}
                                    className="block w-full rounded-md border-0 bg-gray-800 py-2 px-3 text-white shadow-sm ring-1 ring-inset ring-gray-700 placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:text-sm sm:leading-6"
                                    placeholder={key === 'typingTexts' ? "Enter as text (comma separated) or convert..." : ""}
                                />
                                {key === 'typingTexts' && (
                                    <button
                                        onClick={() => {
                                            const val = content[key];
                                            const arr = typeof val === 'string' ? val.split(',').map(s => s.trim()).filter(Boolean) : [];
                                            handleChange(key, arr as any);
                                        }}
                                        className="text-xs text-blue-500 hover:text-blue-400 font-medium flex items-center gap-1"
                                    >
                                        <Plus size={12} /> Convert to Manageable List
                                    </button>
                                )}
                            
                            <ImageUpload
                                value={content[key]}
                                onChange={(url) => handleChange(key, url)}
                                folder="content"
                            />
                            </div>
                        ) : typeof content[key] === 'object' ? (
                            <div>
                                <p className="text-xs text-gray-500 mb-1">JSON Editor</p>
                                <textarea
                                    rows={5}
                                    readOnly
                                    value={JSON.stringify(content[key], null, 2)}
                                    className="block w-full rounded-md border-0 bg-gray-800/50 py-2 px-3 text-gray-400 font-mono text-xs"
                                />
                                <p className="text-xs text-yellow-500 mt-1">Complex data. Use the specific manager for this section.</p>
                            </div>
                        ) : (key.toLowerCase().includes('image') || key.toLowerCase().includes('avatar') || key.toLowerCase().includes('logo')) ? (
                            <ImageUpload
                                value={content[key]}
                                onChange={(url) => handleChange(key, url)}
                                folder="content"
                            />
                        ) : (
                            <input
                                type="text"
                                value={content[key]}
                                onChange={(e) => handleChange(key, e.target.value)}
                                className="block w-full rounded-md border-0 bg-gray-800 py-2 px-3 text-white shadow-sm ring-1 ring-inset ring-gray-700 placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:text-sm sm:leading-6"
                            />
                        )}
                    </div>
                ))}

                {(!content || Object.keys(content).length === 0) && (
                    <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-800 rounded-lg">
                        No content fields found. Initialize this section in the database first.
                    </div>
                )}
            </div>
        </div>
    );
}
